// src/pages/TicketRecall/modals/CreateTicketRecallModal.tsx
// Form lập phiếu thu hồi vé ế (mục 13.3, REQUIRE_FIXBUGS.md) — bản rút gọn của
// CreateTicketDeliveryModal: người lập phiếu lấy từ tài khoản đăng nhập (backend tự gán
// CreatedBy), giữ nguyên chọn kế hoạch + bảng dải số + auto-fill theo ký hiệu. Đại lý là LỰA CHỌN
// (không bắt buộc như giao vé): bỏ trống = thu hồi từ kho tổng (thiết kế ban đầu), chọn 1 đại lý =
// thu hồi vé ế đang nằm ở đại lý đó (đã giao/AgentConfirmed nhưng bán không hết, đại lý trả lại).
//
// Mục 12, README.md — quét QR trên vé là cách nhập CHÍNH cho dải số thu hồi (đọc trực tiếp từ mã
// hóa vé, tránh gõ nhầm số), nhập tay chỉ còn dùng khi không quét được (mất/mờ mã QR...). Mỗi dòng
// có 2 nút quét riêng — quét vé ĐẦU dải cho "Số bắt đầu", quét vé CUỐI dải cho "Số kết thúc" — khớp
// với cách đại lý trả cả 1 xấp vé liên tục (chỉ cần quét 2 đầu mút, không phải quét từng tờ).
// Token quét được xác thực lại đúng cơ chế ký ECDSA của vé (ticketTokenVerifier, ưu tiên xác thực
// offline bằng public key, chỉ rơi xuống gọi API decode khi token là định dạng cũ) — đảm bảo số vé
// điền vào là số THẬT do server ký, không phải chuỗi bất kỳ người dùng dán vào.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Label,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import QrScannerModal from 'pages/TicketLookup/components/QrScannerModal';
import { getAgents, getTicketDeliveredRangesApi, decodeTicketToken } from 'helpers/fakebackend_helper';
import { decodeScannedTicket } from 'helpers/ticketQrDecode';
import { IAgent } from 'slices/agentManagement/types';
import { IssuancePlan } from 'pages/IssuancePlan/types';
import { fetchTicketStockBalances } from 'slices/ticketDelivery/thunk';
import { clearStockBalances } from 'slices/ticketDelivery/reducer';
import {
  DeliveryLineInput,
  TicketStockBalance,
  calcLineQuantity,
  validateDeliveryLine,
} from 'pages/TicketDeliveryManagement/types';
import { CreateTicketRecallPayload } from '../types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_recall');

interface SymbolOption {
  value: string;
  label: string;
}

// Mục 11.a, README_UPDATE.md — 1 dải số đã giao thực sự (không phải tồn kho còn lại) cho đại lý
// đang chọn, dùng để auto-fill khi người dùng chọn ký hiệu.
interface DeliveredRange {
  symbol: string;
  rangeStart: string;
  rangeEnd: string;
  quantity: number;
  deliveryCode: string;
}

interface AgentOption {
  value: number;
  label: string;
}

interface CreateTicketRecallModalProps {
  isOpen: boolean;
  submitting: boolean;
  plans: IssuancePlan[];
  // Danh sách đại lý gợi ý mặc định (đã nạp sẵn từ trang cha qua loadAgentsWithCache) — autocomplete
  // vẫn gọi API riêng khi gõ tìm, giống CreateTicketDeliveryModal.
  agents: IAgent[];
  onClose: () => void;
  onSubmit: (payload: CreateTicketRecallPayload) => void;
}

const emptyLine = (): DeliveryLineInput => ({ symbol: '', rangeStart: '', rangeEnd: '' });

const symbolSelectStyles = {
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  control: (base: any) => ({ ...base, minHeight: 31 }),
};

const formatNumber = (value: number) => value.toLocaleString('vi-VN');

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

const CreateTicketRecallModal: React.FC<CreateTicketRecallModalProps> = ({
  isOpen,
  submitting,
  plans,
  agents,
  onClose,
  onSubmit,
}) => {
  const dispatch = useDispatch();
  const { stockBalances } = useSelector((state: any) => state.TicketDelivery) as {
    stockBalances: TicketStockBalance[];
  };
  // Mục 8/9, REQUIRE_FIXBUGS.md — tài khoản đại lý tự lập phiếu thì khóa cứng ô chọn đại lý = chính
  // mình (không cho tự tìm/chọn đại lý khác); backend cũng ép lại y hệt (không tin riêng FE).
  const currentAgentId = useSelector((state: any) => state.UserProfile?.profile?.agentId);
  const isAgentAccount = !!currentAgentId;
  const [selectedPlanId, setSelectedPlanId] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<AgentOption | null>(null);
  const [agentSearchInput, setAgentSearchInput] = useState('');
  const [defaultAgentOptions, setDefaultAgentOptions] = useState<AgentOption[]>([]);
  const [note, setNote] = useState('');
  const [lines, setLines] = useState<DeliveryLineInput[]>([emptyLine()]);
  const [touched, setTouched] = useState(false);
  // Muc 12, README.md — dong + o (dau/cuoi dai) dang cho ket qua quet, null = modal quet dang dong.
  const [scanTarget, setScanTarget] = useState<{ index: number; field: 'rangeStart' | 'rangeEnd' } | null>(null);
  // Bổ sung — nút "Quét QR" ở thanh công cụ: quét 1 vé đơn -> thêm hẳn 1 dòng mới (bắt đầu = kết thúc),
  // và nếu chưa chọn kế hoạch thì tự set kế hoạch theo kỳ của vé đầu tiên.
  const [scanNewLine, setScanNewLine] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [deliveredRanges, setDeliveredRanges] = useState<DeliveredRange[]>([]);
  const agentSearchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // QrScannerModal ban chat quet lien tuc theo khung hinh (fps: 10) va chi thuc su dung camera SAU
  // khi Modal dong (onClosed) — neu khong chan lai co the nhan duoc nhieu lan goi onScanned cho
  // CUNG 1 ma trong luc dong camera dang dung do. Ref nay dam bao chi xu ly lan quet DAU TIEN cho
  // moi phien quet (reset lai moi lan mo modal o handleOpenScan).
  const scanHandledRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      setSelectedPlanId('');
      // Tài khoản đại lý: khóa cứng đúng đại lý của chính mình, tìm trong danh sách agents đã nạp
      // sẵn từ trang cha (nếu chưa kịp nạp thì hiện tạm "#<id>", vẫn gửi đúng agentId khi submit).
      if (isAgentAccount) {
        const self = agents.find((agent) => Number(agent.id) === Number(currentAgentId));
        setSelectedAgent(
          self
            ? { value: Number(self.id), label: `${self.code} - ${self.name}` }
            : { value: Number(currentAgentId), label: `#${currentAgentId}` },
        );
      } else {
        setSelectedAgent(null);
      }
      setAgentSearchInput('');
      setNote('');
      setLines([emptyLine()]);
      setTouched(false);
      setScanTarget(null);
      setScanning(false);
      setDefaultAgentOptions(
        agents.slice(0, 20).map((agent) => ({ value: Number(agent.id), label: `${agent.code} - ${agent.name}` })),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isAgentAccount, currentAgentId, agents]);

  // Autocomplete đại lý — cùng pattern CreateTicketDeliveryModal (debounce 350ms, gọi API theo Mã/Tên).
  const loadAgentOptions = useCallback(
    (inputValue: string): Promise<AgentOption[]> =>
      new Promise((resolve) => {
        if (agentSearchTimer.current) clearTimeout(agentSearchTimer.current);
        agentSearchTimer.current = setTimeout(async () => {
          try {
            const response = await getAgents({
              PageIndex: 1,
              PageSize: 20,
              Status: 'Active',
              Search: inputValue || undefined,
            });
            resolve(
              (getPayloadItems(response) as IAgent[]).map((agent) => ({
                value: Number(agent.id),
                label: `${agent.code} - ${agent.name}`,
              })),
            );
          } catch (error: any) {
            toast.error(error?.message || st('khong_the_tim_dai_ly_message'));
            resolve([]);
          }
        }, 350);
      }),
    [],
  );

  // Tồn kho theo Symbol của đại lý đang chọn (vé đang giữ, có thể thu hồi lại) — tái dùng nguyên
  // thunk/state đã có ở slices/ticketDelivery. Cần đủ cả kế hoạch lẫn đại lý mới đọc được, vì đại
  // lý là bắt buộc (mục 13.3 đã sửa — không còn "kho tổng").
  useEffect(() => {
    if (!isOpen) return;
    if (!selectedPlanId || !selectedAgent) {
      dispatch(clearStockBalances());
      return;
    }
    dispatch(
      fetchTicketStockBalances({
        planId: Number(selectedPlanId),
        agentId: selectedAgent.value,
      }) as any,
    );
  }, [dispatch, isOpen, selectedPlanId, selectedAgent]);

  // Mục 11.a, README_UPDATE.md — nạp sẵn dải số ĐÃ GIAO thực sự cho đại lý đang chọn (khác tồn kho
  // còn lại ở effect trên), dùng để auto-fill khi chọn ký hiệu bên dưới thay vì phải gõ tay.
  useEffect(() => {
    if (!isOpen || !selectedPlanId || !selectedAgent) {
      setDeliveredRanges([]);
      return;
    }
    let cancelled = false;
    getTicketDeliveredRangesApi({ planId: Number(selectedPlanId), agentId: selectedAgent.value })
      .then((response: any) => {
        if (cancelled) return;
        const payload = response?.data || response;
        setDeliveredRanges((payload?.items || payload?.data || payload || []) as DeliveredRange[]);
      })
      .catch(() => {
        if (!cancelled) setDeliveredRanges([]);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedPlanId, selectedAgent]);

  const symbolOptions = useMemo<SymbolOption[]>(
    () =>
      stockBalances
        .filter((balance) => (balance.quantity || 0) > 0)
        .map((balance) => ({
          value: balance.symbol,
          label: st('symbol_con_value_ve_label', { symbol: balance.symbol, value: formatNumber(balance.quantity || 0) }),
        })),
    [stockBalances],
  );

  const lineErrors = useMemo(() => lines.map((line) => validateDeliveryLine(line)), [lines]);
  const totalQuantity = useMemo(
    () => lines.reduce((sum, line) => sum + calcLineQuantity(line), 0),
    [lines],
  );

  const planError = !selectedPlanId ? st('chua_chon_ke_hoach_phat_hanh_text') : null;
  const agentError = !selectedAgent ? st('chua_chon_dai_ly_dang_bao_text') : null;
  const hasError = !!planError || !!agentError || lineErrors.some((error) => error !== null);

  const handleLineChange = (index: number, field: keyof DeliveryLineInput, value: string) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  };

  const handleAddLine = () => setLines((prev) => [...prev, emptyLine()]);

  const handleRemoveLine = (index: number) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  // Mục 12, README.md — mở modal quét cho đúng dòng + ô (đầu/cuối dải) người dùng bấm vào.
  const handleOpenScan = (index: number, field: 'rangeStart' | 'rangeEnd') => {
    scanHandledRef.current = false;
    setScanTarget({ index, field });
  };

  // Bổ sung — mở camera quét 1 vé đơn để thêm dòng mới (nút "Quét QR" ở thanh công cụ).
  const handleOpenScanNewLine = () => {
    scanHandledRef.current = false;
    setScanNewLine(true);
  };

  const handleScanned = async (decodedText: string) => {
    // Chặn các khung hình quét lặp lại của CÙNG 1 phiên (xem chú thích scanHandledRef) — chỉ dòng
    // đầu tiên đi tiếp, các lần gọi sau trong lúc modal đang đóng camera bị bỏ qua.
    if (scanHandledRef.current || (!scanTarget && !scanNewLine)) return;
    scanHandledRef.current = true;

    const target = scanTarget;
    const isNewLine = scanNewLine;
    setScanTarget(null);
    setScanNewLine(false);
    setScanning(true);
    try {
      const decoded = await decodeScannedTicket(decodedText, decodeTicketToken);
      if (!decoded) {
        toast.error(st('khong_doc_duoc_ma_qr_nay_message'));
        return;
      }

      const wasPlanEmpty = !selectedPlanId;
      if (wasPlanEmpty) {
        // Vé ĐẦU TIÊN — chưa chọn kế hoạch: tự set kế hoạch phát hành theo KỲ của vé vừa quét.
        const matchedPlan = plans.find((plan) => String(plan.id) === String(decoded.lotteryPeriodId));
        if (!matchedPlan) {
          toast.error(
            st('ky_phat_hanh_cua_ve_vua_message')
          );
          return;
        }
        setSelectedPlanId(String(decoded.lotteryPeriodId));
      } else if (decoded.lotteryPeriodId !== Number(selectedPlanId)) {
        toast.error(st('ve_vua_quet_khong_thuoc_ke_message'));
        return;
      }

      // Vừa set kế hoạch / chưa chọn đại lý -> tồn kho (symbolOptions) chưa có, bỏ qua kiểm tra ký
      // hiệu tại đây; bước validate lúc lưu phiếu sẽ chặn. Có tồn kho mới chặn ký hiệu ngoài danh sách.
      if (!wasPlanEmpty && symbolOptions.length > 0 && !symbolOptions.some((option) => option.value === decoded.symbol)) {
        toast.error(
          st('ky_hieu_symbol_cua_ve_vua_message', { symbol: decoded.symbol })
        );
        return;
      }

      if (isNewLine) {
        // Vé đơn: bắt đầu = kết thúc = số vé. Điền vào dòng cuối nếu còn trống, ngược lại thêm dòng mới.
        setLines((prev) => {
          const newLine: DeliveryLineInput = {
            symbol: decoded.symbol,
            rangeStart: decoded.ticketNumber,
            rangeEnd: decoded.ticketNumber,
          };
          const last = prev[prev.length - 1];
          const lastEmpty = last && !last.symbol && !last.rangeStart && !last.rangeEnd;
          return lastEmpty ? [...prev.slice(0, -1), newLine] : [...prev, newLine];
        });
        toast.success(
          wasPlanEmpty
            ? st('da_chon_ke_hoach_theo_ky_message', { symbol: decoded.symbol, ticket_number: decoded.ticketNumber })
            : st('da_them_ve_symbol_ticket_number_message', { symbol: decoded.symbol, ticket_number: decoded.ticketNumber })
        );
        return;
      }

      // Quét vào đúng ô (đầu/cuối dải) mà người dùng đã bấm.
      setLines((prev) =>
        prev.map((line, i) =>
          i === target!.index
            ? { ...line, symbol: decoded.symbol, [target!.field]: decoded.ticketNumber }
            : line
        )
      );
      toast.success(
        st('da_dien_ve_symbol_ticket_number_message', { symbol: decoded.symbol, ticket_number: decoded.ticketNumber, value: target!.field === 'rangeStart' ? st('so_bat_dau_column') : st('so_ket_thuc_column'), value_2: target!.index + 1 })
      );
    } finally {
      setScanning(false);
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (hasError || !selectedAgent) return;

    onSubmit({
      issuancePlanId: Number(selectedPlanId),
      agentId: selectedAgent.value,
      note: note.trim() || undefined,
      lines: lines.map((line) => ({
        symbol: line.symbol.trim(),
        rangeStart: line.rangeStart.trim(),
        rangeEnd: line.rangeEnd.trim(),
      })),
    });
  };

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="lg" centered>
      <ModalHeader toggle={onClose}>{st('lap_phieu_thu_hoi_ve_e_title')}</ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          <Alert color="info" className="py-2">
            <i className="ri-information-line me-1"></i>
            {isAgentAccount ? (
              <>
                {st('ve_thu_hoi_se_bi_tru_text')} <strong>{st('cho_duyet_text')}</strong> {st('can_cap_tren_kiem_tra_va_text')}
              </>
            ) : (
              <>
                {st('dung_khi_dai_ly_bao_lai_text')}{selectedAgent ? <> <strong>{selectedAgent.label}</strong></> : null}{st('khong_hoan_lai_kho_tong_phieu_text')} <strong>{st('duyet_luon_text')}</strong> {st('khi_luu_khong_can_thao_tac_text')}
              </>
            )}
          </Alert>

          <Row className="g-3">
            <Col md={6}>
              <FormGroup>
                <Label for="recall-plan">
                  {st('ke_hoach_phat_hanh_da_nhap_label')} <span className="text-danger">*</span>
                </Label>
                <Input
                  type="select"
                  id="recall-plan"
                  className="form-select"
                  value={selectedPlanId}
                  onChange={(event) => setSelectedPlanId(event.target.value)}
                >
                  <option value="">{st('chon_ke_hoach_phat_hanh_option')}</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.planCode} — {plan.ticketTypeName || plan.ticketType}
                    </option>
                  ))}
                </Input>
                {touched && planError && <div className="text-danger small mt-1">{planError}</div>}
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label for="recall-agent">
                  {st('dai_ly_dang_bao_ve_e_label')} <span className="text-danger">*</span>
                </Label>
                <AsyncSelect
                  inputId="recall-agent"
                  isDisabled={isAgentAccount}
                  isClearable={!isAgentAccount}
                  cacheOptions
                  defaultOptions={defaultAgentOptions}
                  loadOptions={loadAgentOptions}
                  value={selectedAgent}
                  inputValue={agentSearchInput}
                  onInputChange={(value, actionMeta) => {
                    if (actionMeta.action === 'input-change') setAgentSearchInput(value);
                    return value;
                  }}
                  onChange={(option) => {
                    setSelectedAgent(option as AgentOption | null);
                    setAgentSearchInput('');
                  }}
                  placeholder={st('go_ma_hoac_ten_dai_ly_placeholder')}
                  noOptionsMessage={() => st('khong_tim_thay_dai_ly_phu_text')}
                  loadingMessage={() => st('dang_tim_text')}
                />
                {isAgentAccount && (
                  <small className="text-muted">{st('tu_dong_lay_theo_tai_khoan_hint')}</small>
                )}
                {touched && agentError && <div className="text-danger small mt-1">{agentError}</div>}
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label for="recall-note">{st('ghi_chu_label')}</Label>
                <Input
                  type="text"
                  id="recall-note"
                  placeholder={st('ly_do_thu_hoi_khong_bat_placeholder')}
                  maxLength={500}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </FormGroup>
            </Col>
          </Row>

          <div className="d-flex align-items-center justify-content-between mt-3 mb-2">
            <h6 className="mb-0">{st('chi_tiet_dai_so_thu_hoi_title')}</h6>
            <div className="d-flex gap-2">
              <Button color="primary" size="sm" type="button" onClick={handleOpenScanNewLine} disabled={scanning}>
                <i className="ri-qr-scan-2-line align-bottom me-1"></i>
                {st('quet_qr_button')}
              </Button>
              <Button color="primary" outline size="sm" type="button" onClick={handleAddLine}>
                <i className="ri-add-line align-bottom me-1"></i>
                {st('them_dong_button')}
              </Button>
            </div>
          </div>
          <p className="text-muted small mb-2">
            <i className="ri-qr-scan-2-line align-bottom me-1"></i>
            {st('bam_text')} <strong>{st('quet_qr_button')}</strong> {st('de_mo_camera_quet_tung_ve_text')} <strong>{st('dau_dai_text')}</strong> / <strong>{st('cuoi_dai_text')}</strong> {st('bang_nut_quet_canh_moi_o_text')}
          </p>

          {!selectedPlanId || !selectedAgent ? (
            <Alert color="secondary" className="py-2">
              <i className="ri-information-line me-1"></i>
              {st('vui_long_chon_ke_hoach_phat_message')}
            </Alert>
          ) : (
            symbolOptions.length === 0 && (
              <Alert color="warning" className="py-2">
                <i className="ri-alert-line me-1"></i>
                {st('dai_ly_label_khong_con_ky_text', { label: selectedAgent.label })}
              </Alert>
            )
          )}

          <div className="table-responsive">
            <table className="table table-bordered table-sm align-middle mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: 220 }}>{st('ky_hieu_con_ton_column')}</th>
                  <th>{st('so_bat_dau_column')}</th>
                  <th>{st('so_ket_thuc_column')}</th>
                  <th className="text-end" style={{ width: 110 }}>
                    {st('so_luong_column')}
                  </th>
                  <th style={{ width: 60 }}></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line, index) => (
                  <tr key={index}>
                    <td>
                      <Select
                        isClearable
                        options={symbolOptions}
                        value={symbolOptions.find((option) => option.value === line.symbol) || null}
                        onChange={(option: SymbolOption | null) => {
                          // Mục 11.a, README_UPDATE.md — fill sẵn đúng dải số ĐÃ GIAO cho đại lý
                          // (không phải dải số của toàn kỳ) để người dùng chỉ cần kiểm tra/chỉnh sửa.
                          const matches = option
                            ? deliveredRanges.filter((range) => range.symbol === option.value)
                            : [];
                          const firstMatch = matches[0];
                          if (matches.length > 1) {
                            toast.info(
                              st('dai_ly_nay_co_length_dai_message', { length: matches.length, value: option?.value }),
                            );
                          }
                          setLines((prev) =>
                            prev.map((l, i) =>
                              i === index
                                ? {
                                    ...l,
                                    symbol: option?.value || '',
                                    rangeStart: firstMatch?.rangeStart || '',
                                    rangeEnd: firstMatch?.rangeEnd || '',
                                  }
                                : l,
                            ),
                          );
                        }}
                        placeholder={st('go_de_tim_ky_hieu_placeholder')}
                        noOptionsMessage={() => st('khong_co_ky_hieu_con_ton_text')}
                        menuPortalTarget={document.body}
                        styles={symbolSelectStyles}
                      />
                    </td>
                    <td>
                      <InputGroup size="sm">
                        <Button
                          color="success"
                          outline
                          type="button"
                          title={st('quet_ve_dau_dai_title')}
                          disabled={scanning || !selectedPlanId}
                          onClick={() => handleOpenScan(index, 'rangeStart')}
                        >
                          <i className="ri-qr-scan-2-line"></i>
                        </Button>
                        <Input
                          type="text"
                          bsSize="sm"
                          placeholder={st('vd_000001_placeholder')}
                          value={line.rangeStart}
                          onChange={(event) => handleLineChange(index, 'rangeStart', event.target.value)}
                        />
                      </InputGroup>
                    </td>
                    <td>
                      <InputGroup size="sm">
                        <Button
                          color="success"
                          outline
                          type="button"
                          title={st('quet_ve_cuoi_dai_title')}
                          disabled={scanning || !selectedPlanId}
                          onClick={() => handleOpenScan(index, 'rangeEnd')}
                        >
                          <i className="ri-qr-scan-2-line"></i>
                        </Button>
                        <Input
                          type="text"
                          bsSize="sm"
                          placeholder={st('vd_001000_placeholder')}
                          value={line.rangeEnd}
                          onChange={(event) => handleLineChange(index, 'rangeEnd', event.target.value)}
                        />
                      </InputGroup>
                    </td>
                    <td className="text-end fw-semibold">{formatNumber(calcLineQuantity(line))}</td>
                    <td className="text-center">
                      {lines.length > 1 && (
                        <Button
                          color="danger"
                          outline
                          size="sm"
                          className="btn-icon"
                          type="button"
                          title={st('xoa_dong_title')}
                          onClick={() => handleRemoveLine(index)}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <th colSpan={3}>{st('tong_so_ve_thu_hoi_column')}</th>
                  <th className="text-end">{formatNumber(totalQuantity)}</th>
                  <th></th>
                </tr>
              </tfoot>
            </table>
          </div>

          {touched && lineErrors.some((error) => error !== null) && (
            <Alert color="danger" className="mt-3 mb-0 py-2">
              <ul className="mb-0 ps-3">
                {lineErrors.map((error, index) =>
                  error ? (
                    <li key={index}>
                      {st('dong_text')} {index + 1}: {error}
                    </li>
                  ) : null,
                )}
              </ul>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" type="button" onClick={onClose} disabled={submitting}>
            {st('huy_button')}
          </Button>
          <Button color="primary" type="submit" disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {st('dang_luu_text')}
              </>
            ) : (
              st('luu_phieu_text')
            )}
          </Button>
        </ModalFooter>
      </Form>
      <QrScannerModal
        isOpen={!!scanTarget || scanNewLine}
        onClose={() => {
          setScanTarget(null);
          setScanNewLine(false);
        }}
        onScanned={handleScanned}
      />
    </Modal>
  );
};

export default CreateTicketRecallModal;
